// ============================================================================
// JARVISTRAVEL - MIXPANEL ANALYTICS WRAPPER
// Version: 1.0.0
// Typed wrapper around mixpanel-browser. No-ops when the env token is unset,
// so analytics are safe to call in any environment.
// ============================================================================

import mixpanel from 'mixpanel-browser';
import type { Dict } from 'mixpanel-browser';
import { matchPath } from 'react-router-dom';
import { SHARED_JOURNAL_ROUTE } from '@/app/constants';
import { INVITATION_PATHS } from '@/app/utils/invitationLinks';

// -----------------------------------------------------------------------------
// INITIALISATION
// -----------------------------------------------------------------------------

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENABLED = Boolean(TOKEN);

if (typeof window !== 'undefined' && ENABLED) {
  mixpanel.init(TOKEN!, {
    debug: import.meta.env.MODE === 'development',
    track_pageview: false, // we track page views manually via router
    persistence: 'localStorage',
    ignore_dnt: false,
    opt_out_tracking_by_default: true, // EU/CA consent gate — tracking is opt-in
    // The SDK adds the page's full URL to every event by itself, and a URL can
    // carry a credential: a shared journal's token IS its path, and Plaid's
    // OAuth return puts oauth_state_id in the query. The blacklist runs inside
    // track() for every event, before it is queued. A before_send hook would
    // not: the SDK skips hooks for $identify, and for a queued event re-sent
    // after a reload. Page views still carry the path, as `route` (JAR-1561).
    property_blacklist: ['$current_url', '$referrer', '$initial_referrer'],
  });
}

// -----------------------------------------------------------------------------
// IDENTITY
// -----------------------------------------------------------------------------

/** Identify a user (call after login / sign-up). */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  if (!ENABLED) return;
  mixpanel.identify(userId);
  if (traits) {
    mixpanel.people.set(traits);
  }
}

/** Reset identity (call on logout). */
export function resetAnalytics(): void {
  if (!ENABLED) return;
  mixpanel.reset();
}

// -----------------------------------------------------------------------------
// CORE TRACKING
// -----------------------------------------------------------------------------

/** Generic track — prefer the named helpers below. */
export function trackEvent(event: string, properties?: Dict): void {
  if (!ENABLED) return;
  mixpanel.track(event, properties);
}

/** Register super-properties attached to every subsequent event. */
export function registerSuperProperties(properties: Dict): void {
  if (!ENABLED) return;
  mixpanel.register(properties);
}

// -----------------------------------------------------------------------------
// CONSENT GATE (EU/CA compliance)
// -----------------------------------------------------------------------------

/**
 * Opt the user into tracking. Called after the user has given consent.
 * Until this is called, no events are sent (opt_out_tracking_by_default).
 */
export function optInAnalytics(): void {
  if (!ENABLED) return;
  mixpanel.opt_in_tracking();
}

/**
 * Opt the user out of tracking. Stops all event collection.
 */
export function optOutAnalytics(): void {
  if (!ENABLED) return;
  mixpanel.opt_out_tracking();
}

// -----------------------------------------------------------------------------
// NAMED EVENTS
// -----------------------------------------------------------------------------

// Routes whose ids someone could act on, sent as their pattern.
const PATTERN_ROUTES = [SHARED_JOURNAL_ROUTE, INVITATION_PATHS.accept, INVITATION_PATHS.decline];

/**
 * Page / route change viewed. `route` is sent as a path alone, since a query can
 * carry a credential. A shared journal and an invitation go as their route
 * pattern, never their token or ids.
 */
export function trackPageView(route: string, title?: string): void {
  const path = route.replace(/[?#].*/, '');
  const pattern = PATTERN_ROUTES.find((p) => matchPath({ path: p, end: false }, path));
  trackEvent('Page View', { route: pattern ?? path, title });
}

/** User signed up (new account). */
export function trackSignUp(method: 'email' | 'demo'): void {
  trackEvent('Sign Up', { method });
}

/** User logged in. */
export function trackLogin(method: 'email' | 'demo'): void {
  trackEvent('Login', { method });
}

/** User logged out. */
export function trackLogout(): void {
  trackEvent('Logout');
}

/** Trip created (from onboarding or manually). */
export function trackTripCreated(source: 'onboarding' | 'manual'): void {
  trackEvent('Trip Created', { source });
}

/** Receipt uploaded / scanned. */
export function trackReceiptUpload(source: 'camera' | 'gallery' | 'manual'): void {
  trackEvent('Receipt Upload', { source });
}

/** Budget was updated (amount, allocations, or expense added). */
export function trackBudgetUpdate(action: 'set' | 'add_expense' | 'edit_allocation' | 'edit_total'): void {
  trackEvent('Budget Update', { action });
}

/** Itinerary changed (day reorder, activity reorder, activity add/remove). */
export function trackItineraryChange(action: 'day_reorder' | 'activity_reorder' | 'activity_edit' | 'activity_add' | 'activity_remove' | 'rebalance'): void {
  trackEvent('Itinerary Change', { action });
}

/** First-run tour began (fresh account fork, or replayed from Settings help). */
export function trackTourStarted(flow: 'trip' | 'explore', source: 'first_run' | 'settings_replay'): void {
  trackEvent('Tour Started', { flow, source });
}

/** A tour step came on screen — per-step drop-off is the tuning signal. */
export function trackTourStepViewed(flow: 'trip' | 'explore', step: string, index: number): void {
  trackEvent('Tour Step Viewed', { flow, step, index });
}

/** The user left the tour early (Skip / Escape / scrim). */
export function trackTourSkipped(flow: 'trip' | 'explore', step: string, index: number): void {
  trackEvent('Tour Skipped', { flow, step, index });
}

/** The user finished the whole tour. */
export function trackTourCompleted(flow: 'trip' | 'explore'): void {
  trackEvent('Tour Completed', { flow });
}

/** A one-time contextual tip appeared at its point of use. */
export function trackTipShown(tip: string): void {
  trackEvent('Contextual Tip Shown', { tip });
}
